// =============================================================================
// legal-client.ts — Client API CGU fournisseur, DOUBLABLE (Option B, STUB-FIRST).
// =============================================================================
// Miroir des endpoints legal de D-LEGAL-API (apps/api/src/legal — CguReadService
// + CguAcceptanceService). Comme les autres clients, expose une INTERFACE
// (LegalClient) et un stub déterministe ; l'impl HTTP réelle (fetch) viendra
// derrière la MÊME interface sans toucher l'UI (concern transverse différé, D7a
// stub-first cohérent D6).
//
// Alignement front→back (le BACK fait autorité, raison : `contentDisplayed` =
// preuve légale) — shapes VERBATIM de D-LEGAL-API :
//   - GET /legal/cgu/current   → CurrentCguView { cguVersionId, versionNumber,
//                                  pdfRef:{bucket,objectKey,sha256} } (PAS de
//                                  `content` inline, PAS de `publishedAt`).
//   - POST /legal/cgu/acceptances { cguVersionId, contentDisplayed } → { acceptanceId }.
//   - GET /legal/cgu/has-signed → HasSignedView { currentVersionId, hasSigned,
//                                  signedVersionId? }.
//
// L'intentionnalité par lecture (EF-LEG-3, UC-F-002 E1) est gérée CÔTÉ UI (scroll
// complet de l'intégralité du document → `contentDisplayed=true`), puis l'API
// enregistre la signature (CguAcceptance immuable, DE.CguAcceptanceRecorded
// legally-binding). L'UI ne décide pas de la valeur juridique : elle relaie la
// signature et son intention. Chaîne de preuve = `acceptance.cguVersionId` →
// `CguVersion.pdfSha256` (write-once) + scroll complet + acceptation explicite.
//
// DTO = miroirs HTTP (PAS le Published Language).

/** Référence d'un PDF GCS write-once (miroir de CguPdfRef ; sha256 = ancrage probatoire). */
export interface CguPdfRef {
  readonly bucket: string;
  readonly objectKey: string;
  readonly sha256: string;
}

/**
 * Version courante des CGU à afficher pour signature (miroir de CurrentCguView,
 * shape D-LEGAL-API : réf PDF write-once, SANS `content` inline ni `publishedAt`).
 */
export interface CguVersionView {
  /** Identifiant de version (UUID côté API). Load-bearing pour la signature. */
  readonly cguVersionId: string;
  /** Numéro de version lisible, format `CGU-YYYY-MM`. */
  readonly versionNumber: string;
  /** Référence du PDF GCS write-once ; sha256 = ancrage probatoire de `contentDisplayed`. */
  readonly pdfRef: CguPdfRef;
}

/**
 * Statut de signature du fournisseur courant (miroir de HasSignedView). `hasSigned`
 * est relatif à `currentVersionId` (false si seule une version ANTÉRIEURE est
 * signée → T-06 « à signer » quand une nouvelle version sort).
 */
export interface HasSignedView {
  readonly currentVersionId: string;
  readonly hasSigned: boolean;
  readonly signedVersionId?: string;
}

/**
 * Résumé des évolutions (changelog) de la version courante (miroir de CguChangelogView).
 * `changelog` = TEXTE BRUT rendu échappé côté UI (nœud texte React, jamais dangerouslySetInnerHTML).
 */
export interface CguChangelogView {
  readonly cguVersionId: string;
  readonly versionNumber: string;
  readonly changelog: string;
}

/** Body de la signature (miroir du SignBody D-LEGAL-API : `{cguVersionId, contentDisplayed}`). */
export interface AcceptCguInput {
  /** Version effectivement affichée et signée (la courante). */
  readonly cguVersionId: string;
  /**
   * Attestation que le contenu a été INTÉGRALEMENT affiché (intentionnalité par
   * lecture, UC-F-002 E1). Posé `true` UNIQUEMENT après scroll complet + acceptation
   * explicite ; preuve légale conservée par l'API (à NE PAS retirer en « simplifiant »).
   */
  readonly contentDisplayed: boolean;
}

/** Résultat de la signature (miroir : l'API ne renvoie que l'identifiant créé). */
export interface CguAcceptanceResult {
  /** Identifiant de l'acceptation immuable créée côté API. */
  readonly acceptanceId: string;
}

/**
 * Erreur applicative renvoyée par l'API legal (HTTP 4xx). L'UI l'affiche telle
 * quelle ; elle ne réinterprète jamais la règle métier. Le `status` HTTP d'origine
 * est CONSERVÉ (renseigné par l'adaptateur HTTP) pour que la couche présentation
 * puisse DISTINGUER un cas fonctionnel d'un autre — notamment « aucune CGU publiée »
 * (404 = NoPublishedCguError côté API) d'une panne réelle — SANS parser le message.
 * `status` reste `undefined` pour les erreurs synthétiques du double (stub) qui ne
 * transitent pas par HTTP, ou pour un échec réseau sans réponse.
 */
export class LegalApiError extends Error {
  /** Statut HTTP d'origine (ex. 404, 409). `undefined` hors contexte HTTP. */
  public readonly status: number | undefined;

  public constructor(message: string, status?: number) {
    super(message);
    this.name = 'LegalApiError';
    this.status = status;
  }

  /**
   * Vrai lorsque AUCUNE CGU n'est publiée (404 = NoPublishedCguError, cf. mapping API).
   * État TRANSITOIRE côté admin (la session fournisseur EST ouverte ; ce n'est pas une
   * faute du fournisseur) : l'UI le traite en MESSAGE INFORMATIF, jamais en crash technique.
   */
  public get isNoPublishedCgu(): boolean {
    return this.status === 404;
  }
}

/** Contrat du client CGU — miroir des endpoints supplier de D-LEGAL-API. */
export interface LegalClient {
  /** GET /legal/cgu/current — version courante publiée à signer (RÉFÉRENCE PDF, pas les octets). */
  currentCgu(): Promise<CguVersionView>;
  /**
   * GET /legal/cgu/current/pdf — OCTETS du PDF CGU publié courant (ajout S7). Le renderer
   * pdf.js (DocumentViewer) consomme ces octets. Distinct de `currentCgu()` (référence
   * bucket/objectKey/sha256) : ici on récupère le binaire à afficher. Le sha256 figé sur la
   * version (réf) reste l'ancrage probatoire ; ces octets en sont l'intégrité matérialisée.
   */
  currentCguPdf(): Promise<ArrayBuffer>;
  /**
   * GET /legal/cgu/has-signed — statut de signature du fournisseur en session.
   * `hasSigned` est relatif à la version courante (T-06).
   */
  hasSigned(): Promise<HasSignedView>;
  /**
   * GET /legal/cgu/current/changelog — résumé des évolutions de la version courante (DBU §3.1).
   * Texte brut ; l'UI le rend échappé (bandeau de resignature).
   */
  currentCguChangelog(): Promise<CguChangelogView>;
  /**
   * POST /legal/cgu/acceptances — enregistre la signature (immuable). L'API émet
   * `DE.CguAcceptanceRecorded` (legally-binding). Autorité : API. Le FRONT n'envoie
   * `contentDisplayed=true` qu'après scroll complet + acceptation explicite (EF-LEG-3).
   */
  acceptCgu(input: AcceptCguInput): Promise<CguAcceptanceResult>;
}
